import { catchError, from, map, Observable, switchMap, tap } from 'rxjs';
import { AxiosError, AxiosResponse } from 'axios';
import { entitiesPropsFactory, setEntities } from '@ngneat/elf-entities';
import { setPage, updatePaginationData } from '@ngneat/elf-pagination';

import { getWasteDataSource, getWasteFamiliesDataSource } from '@store/settings/wasteFamilies/wasteFamilies.requests';
import { Pagination } from '@store/common/pagination.model';
import { wasteFamiliesStore } from '@store/settings/wasteFamilies/wasteFamilies.store';
import {
  ManageWaste,
  ManageWasteFamily,
  WasteFamiliesPagination,
  WasteFamily,
  WastePagination
} from '@store/settings/wasteFamilies/wasteFamilies.model';

import APIAxios, { APIRoutes } from '@api/axios.api';
import SnackError from '@utils/error.utils';
import { TableConfig } from '@constants/table.constants';
import { Array } from '@utils/extensions/array.extension';

const {wasteEntitiesRef} = entitiesPropsFactory('waste');

export class WasteFamiliesService {
  store = wasteFamiliesStore;

  resetStore = () => this.store.reset();

  getWasteFamilies = (): Observable<WasteFamiliesPagination> => {
    return from(APIAxios({
      ...APIRoutes.GETWasteFamilies(),
      params: {disablePaginate: true},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<WasteFamiliesPagination>) => {
          return response.data;
        }),
        tap((wasteFamiliesPagination) => {
          const paginationData = Pagination.toPaginationData(wasteFamiliesPagination);

          return this.store.update(
            setEntities(wasteFamiliesPagination.data),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, wasteFamiliesPagination.data.map((wasteFamily) => wasteFamily.id)),
            getWasteFamiliesDataSource.setSuccess(),
          );
        }),
        getWasteFamiliesDataSource.trackRequestStatus(),
      );
  };

  searchWasteFamilies = (search?: string): Observable<WasteFamiliesPagination> => {
    return from(APIAxios({
      ...APIRoutes.GETWasteFamilies(),
      params: {
        searchString: search || undefined,
        page: 1,
        limit: TableConfig.pagination,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<WasteFamiliesPagination>) => {
          return response.data;
        }),
      );
  };

  createWasteFamily = (data: ManageWasteFamily): Observable<WasteFamiliesPagination> => {
    return from(APIAxios({
      ...APIRoutes.POSTWasteFamily(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<WasteFamily>) => {
          return response.data;
        }),
        switchMap(() => this.getWasteFamilies()),
      );
  };

  updateWasteFamily = (wasteFamilyId: number | string, data: ManageWasteFamily): Observable<WasteFamiliesPagination> => {
    return from(APIAxios({
      ...APIRoutes.PUTWasteFamily(wasteFamilyId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<WasteFamily>) => {
          return response.data;
        }),
        switchMap(() => this.getWasteFamilies()),
      );
  };

  deleteWasteFamily = (wasteFamilyId: number | string): Observable<WasteFamiliesPagination> => {
    return from(APIAxios(APIRoutes.DELETEWasteFamily(wasteFamilyId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getWasteFamilies()),
      );
  };

  searchWaste = (search?: string, wasteFamilyIds?: (string | undefined)[]): Observable<WastePagination> => {
    const filteredWasteFamilyIds = Array.mapNotNull(wasteFamilyIds);
    return from(APIAxios({
      ...APIRoutes.GETAllWaste(),
      params: {
        searchString: search || undefined,
        page: 1,
        limit: TableConfig.pagination,
        wasteFamilyIds: Array.formatJson(filteredWasteFamilyIds),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<WastePagination>) => {
          return response.data;
        }),
      );
  };

  getAllWaste = (): Observable<WastePagination> => {
    return from(APIAxios({
      ...APIRoutes.GETAllWaste(),
      params: {disablePaginate: true},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<WastePagination>) => {
          return response.data;
        }),
        tap((wastePagination) => {
          this.store.update(
            setEntities(wastePagination.data, {ref: wasteEntitiesRef}),
            getWasteDataSource.setSuccess(),
          );
        }),
        getWasteDataSource.trackRequestStatus(),
      );
  };

  createWaste = (wasteFamilyId: number, data: ManageWaste): Observable<WastePagination> => {
    return from(APIAxios({
      ...APIRoutes.POSTWaste(),
      data: {
        ...data,
        wasteFamilyId,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<WasteFamily>) => {
          return response.data;
        }),
        switchMap(() => this.getAllWaste()),
      );
  };

  updateWaste = (wasteId: number | string, data: ManageWaste): Observable<WastePagination> => {
    return from(APIAxios({
      ...APIRoutes.PUTWaste(wasteId),
      data: {
        ...data,
        co2emission: data.co2emission ?? 0,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<WasteFamily>) => {
          return response.data;
        }),
        switchMap(() => this.getAllWaste()),
      );
  };

  deleteWaste = (wasteId: number | string): Observable<WastePagination> => {
    return from(APIAxios(APIRoutes.DELETEWaste(wasteId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getAllWaste()),
      );
  };
}

export const wasteFamiliesService = new WasteFamiliesService();
